import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Trash2, Utensils, ShoppingBasket, GripVertical } from "lucide-react";
import {
  DndContext, MouseSensor, TouchSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay, closestCenter,
} from "@dnd-kit/core";
import { plannerAPI, sundayOf, ymd, addDays, recipePhotoUrl } from "@/lib/planner";
import { toast } from "sonner";
import MealPicker from "@/components/planner/MealPicker";
import CopyWeekDialog from "@/components/planner/CopyWeekDialog";
import MealTypeTabs from "@/components/planner/MealTypeTabs";

export default function PlannerMeals() {
  const [weekStart, setWeekStart] = useState(sundayOf(new Date()));
  const [meals, setMeals] = useState({ entries: [] });
  const [loading, setLoading] = useState(true);
  const [pickerFor, setPickerFor] = useState(null); // { date }
  const [copyWeekOpen, setCopyWeekOpen] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [mealType, setMealType] = useState("dinner");

  const loadMeals = async (w = weekStart) => {
    setLoading(true);
    try {
      const d = await plannerAPI.meals(ymd(w));
      setMeals(d);
    } catch { toast.error("Couldn't load your meal plan."); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadMeals(); }, [weekStart]);

  const openPicker = (dateYMD) => setPickerFor({ date: dateYMD });

  const removeMeal = async (id) => {
    try {
      await plannerAPI.deleteMeal(id);
      await loadMeals();
    } catch { toast.error("Couldn't remove."); }
  };

  // Drag-to-swap — same wiring as /planner/home, but rendered against the
  // full-width list. A small drag distance keeps taps on the Open / Remove
  // buttons snappy.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );
  const handleDragStart = (e) => {
    const src = visibleEntries.find((m) => m.date === e.active.id);
    if (!src) return;
    setDragging({
      date: e.active.id,
      title: src.recipe?.title || "Recipe",
      photo: src.recipe?.photo_path || null,
    });
  };
  const handleDragEnd = async (e) => {
    setDragging(null);
    const from = e.active?.id;
    const to = e.over?.id;
    if (!from || !to || from === to) return;

    // Optimistic swap — scoped to the currently visible meal_type only.
    const prev = meals.entries || [];
    const next = prev.map((m) => {
      const mt = m.meal_type || "dinner";
      if (mt !== mealType) return m;
      if (m.date === from) return { ...m, date: to };
      if (m.date === to) return { ...m, date: from };
      return m;
    });
    setMeals((cur) => ({ ...cur, entries: next }));

    try {
      await plannerAPI.swapMeals(from, to, mealType);
      await loadMeals(weekStart);
    } catch {
      toast.error("Couldn't swap those meals.");
      await loadMeals(weekStart);
    }
  };

  // Meals for the currently-selected tab + per-tab counts for the tab badge.
  const allEntries = meals.entries || [];
  const counts = useMemo(() => {
    const c = { breakfast: 0, lunch: 0, dinner: 0 };
    for (const e of allEntries) c[e.meal_type || "dinner"] += 1;
    return c;
  }, [allEntries]);
  const visibleEntries = useMemo(
    () => allEntries.filter((e) => (e.meal_type || "dinner") === mealType),
    [allEntries, mealType],
  );

  const weekEnd = addDays(weekStart, 6);
  const label = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  const shift = (n) => setWeekStart(addDays(weekStart, n * 7));

  return (
    <div data-testid="planner-meals">
      <div className="mb-4 sm:mb-6">
        <div className="flex items-start justify-between gap-3 mb-3 sm:mb-4">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">MEAL PLAN</div>
            <h1 className="text-2xl sm:text-4xl text-[#2A2823] mt-1 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{label}</h1>
          </div>
          {/* Primary CTA — always visible, tap-friendly. Text collapses to
              just "Basket" under sm so it stays on a single line beside
              the title on phones. */}
          <Link
            to={`/planner/shopping?week=${ymd(weekStart)}`}
            data-testid="planner-meals-shopping"
            className="inline-flex items-center gap-1.5 text-sm rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-3 sm:px-4 h-9 shrink-0"
          >
            <ShoppingBasket className="w-4 h-4" />
            <span className="hidden sm:inline">Open my basket</span>
            <span className="sm:hidden text-xs">Basket</span>
          </Link>
        </div>
        {/* Secondary controls — prev/this-week/next + Reuse. Wraps on
            small screens; Reuse's label shortens. */}
        <div className="flex items-center gap-2 flex-wrap">
          <button aria-label="Previous week" data-testid="planner-meals-prev" onClick={() => shift(-1)} className="w-9 h-9 rounded-full border border-[#A87F56]/30 flex items-center justify-center hover:bg-[#A87F56]/10">
            <ChevronLeft className="w-4 h-4 text-[#3E4A37]" />
          </button>
          <button data-testid="planner-meals-this-week" onClick={() => setWeekStart(sundayOf(new Date()))} className="text-sm rounded-full border border-[#A87F56]/30 px-4 h-9 text-[#3E4A37] hover:bg-[#A87F56]/10">
            This week
          </button>
          <button aria-label="Next week" data-testid="planner-meals-next" onClick={() => shift(1)} className="w-9 h-9 rounded-full border border-[#A87F56]/30 flex items-center justify-center hover:bg-[#A87F56]/10">
            <ChevronRight className="w-4 h-4 text-[#3E4A37]" />
          </button>
          <button
            onClick={() => setCopyWeekOpen(true)}
            data-testid="planner-meals-copy-week"
            className="text-sm rounded-full border border-[#A87F56]/30 px-4 h-9 text-[#3E4A37] hover:bg-[#A87F56]/10"
          >
            <span className="hidden sm:inline">Reuse a past week</span>
            <span className="sm:hidden">Reuse week</span>
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <MealTypeTabs value={mealType} onChange={setMealType} counts={counts} testidPrefix="planner-meals-meal-type" />
        <div className="text-xs text-[#2A2823]/50">Drag a tile onto another day to swap — within this tab.</div>
      </div>

      <div className="bg-white rounded-2xl border border-[#A87F56]/25 overflow-hidden">
        {loading ? (
          <div className="py-8 text-center text-sm text-[#2A2823]/50">Loading…</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDragging(null)}
          >
            <ul className="divide-y divide-[#A87F56]/15">
              {[...Array(7)].map((_, i) => {
                const d = addDays(weekStart, i);
                const key = ymd(d);
                const entry = visibleEntries.find((m) => m.date === key);
                return (
                  <MealsListRow
                    key={key}
                    date={key}
                    dateObj={d}
                    entry={entry}
                    onAdd={() => openPicker(key)}
                    onRemove={() => entry && removeMeal(entry.id)}
                  />
                );
              })}
            </ul>
            <DragOverlay dropAnimation={null}>
              {dragging ? (
                <div className="flex items-center gap-3 py-2 px-3 bg-white rounded-xl border border-[#A87F56]/40 shadow-lg">
                  {dragging.photo ? (
                    <img
                      src={recipePhotoUrl(dragging.photo)}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover bg-[#EDE5DA]"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#EDE5DA] flex items-center justify-center">
                      <Utensils className="w-4 h-4 text-[#3E4A37]/60" />
                    </div>
                  )}
                  <div className="text-sm text-[#2A2823] max-w-[220px] truncate">{dragging.title}</div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Recipe picker */}
      <MealPicker
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        date={pickerFor?.date}
        mealType={mealType}
        onSaved={loadMeals}
      />

      <CopyWeekDialog
        open={copyWeekOpen}
        onClose={() => setCopyWeekOpen(false)}
        targetWeek={ymd(weekStart)}
        onCopied={() => loadMeals(weekStart)}
      />
    </div>
  );
}

function MealsListRow({ date, dateObj, entry, onAdd, onRemove }) {
  const draggable = useDraggable({ id: date, disabled: !entry });
  const droppable = useDroppable({ id: date });
  const setRefs = (node) => {
    draggable.setNodeRef(node);
    droppable.setNodeRef(node);
  };
  const dropOver = droppable.isOver && draggable.isDragging === false;

  return (
    <li
      ref={setRefs}
      className={`group flex items-center gap-4 p-4 transition ${
        draggable.isDragging ? "opacity-30" : ""
      } ${dropOver ? "bg-[#A87F56]/10" : ""}`}
      data-testid={`planner-meals-day-${date}`}
    >
      <div className="w-14 text-center shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-[#2A2823]/50">
          {dateObj.toLocaleDateString(undefined, { weekday: "short" })}
        </div>
        <div className="text-2xl text-[#2A2823]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {dateObj.getDate()}
        </div>
      </div>
      {entry ? (
        <>
          {/* Drag handle wraps the thumb + a small grip icon on hover so it
              reads as "grab here to move". Buttons stay outside the handle. */}
          <div
            {...draggable.listeners}
            {...draggable.attributes}
            className="shrink-0 cursor-grab active:cursor-grabbing touch-none relative"
            aria-label={`Drag meal for ${dateObj.toLocaleDateString()}`}
            data-testid={`planner-meals-drag-${date}`}
          >
            {entry.recipe?.photo_path ? (
              <img
                src={recipePhotoUrl(entry.recipe.photo_path)}
                alt={entry.recipe.title}
                className="w-14 h-14 rounded-lg object-cover bg-[#EDE5DA] pointer-events-none"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                draggable={false}
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-[#EDE5DA] flex items-center justify-center">
                <Utensils className="w-5 h-5 text-[#3E4A37]/60" />
              </div>
            )}
            <GripVertical
              className="absolute -left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A87F56] opacity-0 group-hover:opacity-100 transition"
              strokeWidth={2}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] text-[#2A2823] truncate">
              {entry.recipe?.title || "Recipe unavailable"}
            </div>
            {entry.recipe && (
              <div className="text-xs text-[#2A2823]/55 mt-0.5">
                {[entry.recipe.servings ? `Serves ${entry.recipe.servings}` : null, entry.recipe.total_time].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <Link
            to={`/idea/${entry.idea_id}`}
            className="text-xs text-[#3E4A37] underline underline-offset-2"
          >
            Open
          </Link>
          <button
            aria-label="Remove meal"
            onClick={onRemove}
            data-testid={`planner-meals-remove-${date}`}
            className="text-[#2A2823]/40 hover:text-[#8a3a2a]"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      ) : (
        <button
          onClick={onAdd}
          data-testid={`planner-meals-add-${date}`}
          className="flex-1 text-left text-sm italic text-[#2A2823]/45 hover:text-[#3E4A37] flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add a meal
        </button>
      )}
    </li>
  );
}
